import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ExportAsExcel } from "@siamf/react-export";
import { Row } from "@tanstack/react-table";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { showError } from "toast/toast";
import {
  fetchSignageProjectsList,
  getLinkedOrderViewPath,
  getProjectAgentOrTeamMemberName,
  SignageProjectListItem,
  SignageProjectsListCounts,
} from "components/proejctsandquoteapicalls";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import "./projectandquote.css";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

type ProjectStatus = "IN_PROGRESS" | "DESIGN_APPROVED" | "QUOTED" | "QUOTE_APPROVED" | "COMPLETED";

function fuzzyTextFilter<TData>(row: Row<TData>, columnId: string, filterValue: string) {
  const raw = row.getValue(columnId);
  const value = String(raw ?? "").toLowerCase();
  return value.includes(String(filterValue).toLowerCase());
}

const statusLabelMap: Record<ProjectStatus, string> = {
  IN_PROGRESS: "In Progress",
  DESIGN_APPROVED: "Design Approved",
  QUOTED: "Quoted",
  QUOTE_APPROVED: "Quote Approved",
  COMPLETED: "Completed",
};

const statusClassMap: Record<ProjectStatus, string> = {
  IN_PROGRESS: "in-progress",
  DESIGN_APPROVED: "design-approved",
  QUOTED: "quoted",
  QUOTE_APPROVED: "quote-approved",
  COMPLETED: "completed",
};

function apiStatusToKey(status: string | undefined): ProjectStatus {
  const s = (status || "").toLowerCase();
  if (s === "in_progress") return "IN_PROGRESS";
  if (s === "design_approved") return "DESIGN_APPROVED";
  if (s === "quoted") return "QUOTED";
  if (s === "quote_approved") return "QUOTE_APPROVED";
  if (s === "completed") return "COMPLETED";
  return "IN_PROGRESS";
}

function formatProjectDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function statusFilterToApiParam(s: "ALL" | ProjectStatus): string | undefined {
  if (s === "ALL") return undefined;
  const map: Record<ProjectStatus, string> = {
    IN_PROGRESS: "in_progress",
    DESIGN_APPROVED: "design_approved",
    QUOTED: "quoted",
    QUOTE_APPROVED: "quote_approved",
    COMPLETED: "completed",
  };
  return map[s];
}

interface ProjectExportRow {
  "Project ID": string;
  "Project Title": string;
  "Agent Name": string;
  "Last Message": string;
  Status: string;
  "Waiting On": string;
  "Last Update": string;
  Designer: string;
}

const columnHelper = createColumnHelper<SignageProjectListItem>();

function getProjectViewPath(projectsBase: string, row: SignageProjectListItem): string | null {
  const projectId = String(row.id ?? "").trim();
  if (!projectId) return null;
  return `${projectsBase}/view/${encodeURIComponent(projectId)}`;
}

const Signageprojectandquotes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectsBase = location.pathname.includes("/signageteammember")
    ? "/signageteammember/projects"
    : "/signage/projects";
  const orderBaseRoute = location.pathname.includes("/signageteammember")
    ? "/signageteammember"
    : "/signage";

  const token = getAuthCookie("sessionToken") || "";
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.projects
  );
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [status, setStatus] = useState<"ALL" | ProjectStatus>("ALL");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [projects, setProjects] = useState<SignageProjectListItem[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [counts, setCounts] = useState<SignageProjectsListCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetLabel, setDeleteTargetLabel] = useState("");
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [globalFilter, status]);

  const loadProjects = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    const showInitialLoader = !hasLoadedDataRef.current;
    if (!token) {
      if (requestId !== fetchRequestIdRef.current) return;
      setProjects([]);
      setRowCount(0);
      setCounts(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await fetchSignageProjectsList(token, {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter.trim() || undefined,
        status: statusFilterToApiParam(status),
      });
      if (requestId !== fetchRequestIdRef.current) return;
      setProjects(result.data);
      setRowCount(result.total);
      setCounts(result.counts);
      hasLoadedDataRef.current = true;
    } catch (e) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error(e);
      showError(e instanceof Error ? e.message : "Failed to load projects");
      setProjects([]);
      setRowCount(0);
      setCounts(null);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [token, pagination.pageIndex, pagination.pageSize, globalFilter, status]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const handleDeleteClick = useCallback((row: SignageProjectListItem) => {
    setDeleteTargetId(row.id);
    setDeleteTargetLabel(row.title || row.project_number || row.id);
    setOpenDelete(true);
  }, []);

  const confirmDeleteProject = useCallback(async () => {
    if (!deleteTargetId) return;
    setIsDeletingProject(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/projects/${encodeURIComponent(deleteTargetId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const raw = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          (raw as any)?.error || (raw as any)?.message || "Failed to delete project";
        throw new Error(typeof message === "string" ? message : "Failed to delete project");
      }
      await loadProjects();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to delete project");
    } finally {
      setIsDeletingProject(false);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetLabel("");
    }
  }, [deleteTargetId, token, loadProjects]);

  // useEffect(() => {
  //   if (!token) return;
  //   const pollId = window.setInterval(() => {
  //     void loadProjects();
  //   }, 15000);
  //   return () => window.clearInterval(pollId);
  // }, [token, loadProjects]);

  const stats = useMemo(() => {
    if (counts) {
      return {
        inProgress: counts.in_progress,
        designApproved: counts.design_approved,
        quoted: counts.quoted,
        quoteApproved: counts.quote_approved,
        completed: counts.completed,
      };
    }
    return { inProgress: 0, designApproved: 0, quoted: 0, quoteApproved: 0, completed: 0 };
  }, [counts]);

  const exportRows = useMemo<ProjectExportRow[]>(
    () =>
      projects.map((row) => {
        const st = apiStatusToKey(row.status);
        const wo = row.waiting_on;
        return {
          "Project ID": row.project_number || row.id,
          "Project Title": row.title || "—",
          "Agent Name": getProjectAgentOrTeamMemberName(row),
          "Last Message": row.last_message?.message || "—",
          Status: statusLabelMap[st],
          "Waiting On":
            wo && wo.toLowerCase() !== "none" ? wo : "—",
          "Last Update": formatProjectDate(row.last_update),
          Designer: (() => {
            const st = apiStatusToKey(row.status);
            if (st === "IN_PROGRESS") {
              return row.designer_name?.trim() || "—";
            }
            return row.designer_name?.trim() || "—";
          })(),
        };
      }),
    [projects]
  );

  const navigateToProjectView = useCallback(
    (row: SignageProjectListItem) => {
      const path = getProjectViewPath(projectsBase, row);
      if (!path) return;
      navigate(path);
    },
    [navigate, projectsBase]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.project_number, {
        id: "project_number",
        header: () => <div className="th-text">Project Number</div>,
        cell: (info) => {
          const row = info.row.original;
          const label = String(info.getValue() ?? row.project_number ?? row.id ?? "").trim() || "—";
          const path = getProjectViewPath(projectsBase, row);
          if (!path || label === "—") {
            return <div className="text-sm">{label}</div>;
          }
          return (
            <Link to={path} className="text-sm order-link-1">
              {label}
            </Link>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor((row) => row.linked_order_number, {
        id: "linked_order_number",
        header: () => <div className="th-text">Order Number</div>,
        cell: (info) => {
          const row = info.row.original;
          const label = String(info.getValue() ?? row.linked_order_number ?? "").trim();
          if (!label) {
            return <div className="text-sm">—</div>;
          }
          const path = getLinkedOrderViewPath(orderBaseRoute, row);
          if (!path) {
            return <div className="text-sm">{label}</div>;
          }
          return (
            <Link to={path} className="text-sm order-link-1">
              {label}
            </Link>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor((row) => row.title ?? "—", {
        id: "title",
        header: () => <div className="th-text">Project Title</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor((row) => getProjectAgentOrTeamMemberName(row), {
        id: "agent_name",
        header: () => <div className="th-text">Agent/Team Member Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor((row) => row.last_message?.message ?? "—", {
        id: "last_message",
        header: () => <div className="th-text">Last Message</div>,
        cell: (info) => {
          const v = info.getValue<string>();
          const short = v.length > 80 ? `${v.slice(0, 80)}…` : v;
          const row = info.row.original;
          const unread = Boolean(row.has_unread);
          return (
            <button
              type="button"
              className="pq-last-message-btn"
              onClick={() => navigate(`${projectsBase}/view/${row.id}`)}
              title={v}
              aria-label={unread ? `${v} (unread)` : v}
            >
              {unread ? <span className="pq-live-dot" aria-hidden /> : null}
              <span
                className={`pq-last-message-text${unread ? " pq-last-message-text--unread" : ""}`}
              >
                {short}
              </span>
            </button>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor((row) => row.status ?? "", {
        id: "Project Status",
        header: () => <div className="th-text">Project Status</div>,
        cell: (info) => {
          const key = apiStatusToKey(info.getValue());
          const row = info.row.original;
          const showApprovedUnreadDot =
            (key === "DESIGN_APPROVED" || key === "QUOTE_APPROVED") &&
            row.approved_update_seen_by_vendor === false;
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span className={`pq-badge ${statusClassMap[key]}`}>{statusLabelMap[key]}</span>
              {showApprovedUnreadDot ? (
                <span className="pq-live-dot pq-live-dot--approved" aria-hidden />
              ) : null}
            </span>
          );
        },
        enableGlobalFilter: false,
      }),
      columnHelper.accessor((row) => row.payment_status ?? "", {
        id: "Payment Status",
        header: () => <div className="th-text">Payment Status</div>,
        cell: (info) => {
          const paymentStatus = info.getValue();
          return (
            <div className="text-sm">
              {paymentStatus ? (
                <SalesOrderStatusBadge type="payment" value={paymentStatus} />
              ) : (
                "-"
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }),
      // columnHelper.accessor((row) => row.waiting_on ?? "—", {
      //   id: "waiting_on",
      //   header: () => <div className="th-text">Waiting On</div>,
      //   cell: (info) => {
      //     const v = info.getValue<string>();
      //     const show = v && v.toLowerCase() !== "none" ? v : "—";
      //     return <div className="text-sm">{show}</div>;
      //   },
      //   filterFn: fuzzyTextFilter,
      //   enableGlobalFilter: true,
      // }),
     
      columnHelper.accessor((row) =>(row.assigned_to_name), {
        id: "assigned_to_name",
        header: () => <div className="th-text">Assigned to Quote</div>,
        cell: (info) => <div className="text-sm">{info.getValue() || "—"}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor((row) => {
        const st = apiStatusToKey(row.status);
        if (st === "IN_PROGRESS") {
          return row.designer_name?.trim() || "—";
        }
        return row.designer_name?.trim() || "—";
      }, {
        id: "designer_name",
        header: () => <div className="th-text">Designer</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
       columnHelper.accessor((row) => formatProjectDate(row.last_update), {
        id: "last_update",
        header: () => <div className="th-text">Last Update</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text d-flex justify-content-center">Actions</div>,
        cell: (info) => {
          const id = info.row.original.id;
          if (!canView && !canEdit && !canDelete) return null;
          return (
            <div className="d-flex justify-content-center gap-2">
              {canView && (
                <ViewIconButton
                  to={`${projectsBase}/view/${id}`}
                  title="View"
                  className="icon-square"
                />
              )}
              {canEdit && (
                <EditIconButton
                  to={`${projectsBase}/edit/${id}`}
                  title="Edit"
                  className="icon-square"
                />
              )}
              {canDelete && (
                <DeleteIconButton
                  title="Delete"
                  onClick={() => handleDeleteClick(info.row.original)}
                />
              )}
            </div>
          );
        },
      }),
    ],
    [projectsBase, orderBaseRoute, handleDeleteClick, navigate, canView, canEdit, canDelete]
  );

  const pageCount = Math.max(1, Math.ceil(rowCount / pagination.pageSize) || 1);

  const table = useReactTable({
    data: projects,
    columns,
    pageCount,
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const showTableLoader = loading;

  return (
    <div className="project-quote-page">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0"></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <button
              className="btn btn-primary table-addbutton"
              onClick={() => navigate(`${projectsBase}/create`)}
              type="button"
            >
              + Create a Project
            </button>
          )}
        </div>
      </div>

      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Projects</h4>
            <p className="pq-page-subtitle mb-0">
              Manage design conversations and quotations with agents
            </p>
          </div>

          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                className="form-control"
                type="search"
                placeholder="Search projects…"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>

            <button
              type="button"
              className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="project-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            {/* <ExportAsExcel
              data={exportRows}
              headers={[
                "Project ID",
                "Project Title",
                "Agent Name",
                "Last Message",
                "Status",
                "Waiting On",
                "Last Update",
                "Designer",
              ]}
              fileName="Projects"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel> */}
          </div>
        </div>

        <div id="project-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-md-4">
                <select
                  className="form-select po-status-select"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as "ALL" | ProjectStatus);
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                  }}
                  style={{ width: "170px", height: "38px" ,float: "right"}}
                >
                  <option value="ALL">All Status</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="DESIGN_APPROVED">Design Approved</option>
                  <option value="QUOTED">Quoted</option>
                  <option value="QUOTE_APPROVED">Quote Approved</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pq-stats-grid">
        <div className="pq-card pq-stat in-progress">
          <div className="pq-stat-top">
            <div className="pq-stat-value">{stats.inProgress}</div>
            <span className="pq-stat-icon">
              <i className="ri-time-line" />
            </span>
          </div>
          <div className="pq-stat-label">In Progress</div>
        </div>
        <div className="pq-card pq-stat approved">
          <div className="pq-stat-top">
            <div className="pq-stat-value">{stats.designApproved}</div>
            <span className="pq-stat-icon">
              <i className="ri-checkbox-circle-line" />
            </span>
          </div>
          <div className="pq-stat-label">Design Approved</div>
        </div>
        <div className="pq-card pq-stat quoted">
          <div className="pq-stat-top">
            <div className="pq-stat-value">{stats.quoted}</div>
            <span className="pq-stat-icon">
              <i className="ri-money-dollar-circle-line" />
            </span>
          </div>
          <div className="pq-stat-label">Quoted</div>
        </div>
        <div className="pq-card pq-stat quote-approved">
          <div className="pq-stat-top">
            <div className="pq-stat-value">{stats.quoteApproved}</div>
            <span className="pq-stat-icon">
              <i className="ri-shield-check-line" />
            </span>
          </div>
          <div className="pq-stat-label">Quote Approved</div>
        </div>
        <div className="pq-card pq-stat completed">
          <div className="pq-stat-top">
            <div className="pq-stat-value">{stats.completed}</div>
            <span className="pq-stat-icon">
              <i className="ri-archive-line" />
            </span>
          </div>
          <div className="pq-stat-label">Completed</div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading projects…" variant="dots" />
              ) : (
                <DesktopTableParts table={table} onRowClick={navigateToProjectView} />
              )}
            </table>
          </div>
        </div>

        <div className="rdt-mobile shadow-sm">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading projects…" variant="dots" />
          ) : (
            <MobileCardsOnly
              table={table}
              idColId="project_number"
              titleColId="title"
              actionColId="actions"
              onRowClick={navigateToProjectView}
            />
          )}
        </div>
      </div>

      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} totalItems={rowCount} />
        </div>
        <p className="pq-muted" style={{ marginTop: 10 }}>
          Showing {projects.length} of {rowCount} results
          {pagination.pageSize ? ` (page ${pagination.pageIndex + 1})` : ""}
        </p>
      </DataTableStatus>

      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetLabel || "Project"}
        showTrashIcon
        isDeleting={isDeletingProject}
        onCancel={() => {
          if (isDeletingProject) return;
          setOpenDelete(false);
          setDeleteTargetId(null);
          setDeleteTargetLabel("");
        }}
        onDelete={confirmDeleteProject}
      />
    </div>
  );
};

export default Signageprojectandquotes;
